import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { FiEdit2, FiInfo, FiPlus, FiPower, FiTrash2 } from "react-icons/fi";
import axios from "../../api/axiosInstance";
import { usePmsTheme } from "../../Components/PmsThemeContext";
import { useOnboardingFlow } from "../OnboardingFlowContext";
import { getOnboardingMessages } from "../onboardingI18n";
import { getExtrasDiscountsMessages } from "../extrasDiscountsMessages";
import {
  currencyAmountStep,
  formatCurrencyAmount,
  getCurrencyMinorUnits,
  hasValidCurrencyPrecision,
  normalizeCurrencyCode,
} from "../../currency/currencyPrecision";
import "../OnboardingApp.css";

const NO_EXPIRY_DATE = "2099-12-31";

function createEmptyRow(currency = "") {
  return {
    id: null,
    row_key: null,
    name: "",
    description: "",
    currency: normalizeCurrencyCode(currency),
    charge_period: "per_stay",
    charge_unit: "per_room",
    scope: "per_room",
    min_nights: "",
    max_nights: "",
    min_pax: "",
    max_pax: "",
    amount: "",
    amount_type: "absolute",
    inclusive: false,
    apply_automatically: false,
    is_mandatory: false,
    room_category_ids: [],
    room_rate_ids: [],
    valid_from: "",
    valid_to: "",
    is_active: true,
  };
}

function normalizeIdArray(values) {
  return Array.isArray(values)
    ? values.map((value) => Number(value)).filter((value) => Number.isInteger(value) && value > 0)
    : [];
}

function normalizeIncomingRow(row, index, fallbackCurrency = "") {
  const source = row && typeof row === "object" ? row : {};
  const id = Number.isInteger(Number(source.id)) && Number(source.id) > 0 ? Number(source.id) : null;
  return {
    ...createEmptyRow(),
    ...source,
    id,
    row_key: id ? `persisted-${id}` : `client-${index + 1}`,
    currency: normalizeCurrencyCode(source.currency || fallbackCurrency),
    charge_period: String(source.charge_period || "per_stay"),
    charge_unit: String(source.charge_unit || "per_room"),
    scope: String(source.scope || "per_room"),
    min_nights: source.min_nights === null || typeof source.min_nights === "undefined" ? "" : String(source.min_nights),
    max_nights: source.max_nights === null || typeof source.max_nights === "undefined" ? "" : String(source.max_nights),
    min_pax: source.min_pax === null || typeof source.min_pax === "undefined" ? "" : String(source.min_pax),
    max_pax: source.max_pax === null || typeof source.max_pax === "undefined" ? "" : String(source.max_pax),
    amount: source.amount === null || typeof source.amount === "undefined" ? "" : String(source.amount),
    room_category_ids: normalizeIdArray(source.room_category_ids),
    room_rate_ids: normalizeIdArray(source.room_rate_ids),
    valid_from: source.valid_from ? String(source.valid_from).slice(0, 10) : "",
    valid_to: source.valid_to ? String(source.valid_to).slice(0, 10) : "",
    inclusive: Boolean(source.inclusive),
    apply_automatically: Boolean(source.apply_automatically),
    is_mandatory: Boolean(source.is_mandatory),
    is_active: source.is_active !== false,
  };
}

function normalizeReferences(references) {
  const source = references && typeof references === "object" ? references : {};
  return {
    room_categories: Array.isArray(source.room_categories) ? source.room_categories : [],
    room_rates: Array.isArray(source.room_rates) ? source.room_rates : [],
  };
}

function serializeRows(rows) {
  return rows.map((row, index) => ({
    id: row.id || null,
    name: String(row.name || "").trim(),
    description: String(row.description || "").trim(),
    currency: normalizeCurrencyCode(row.currency),
    charge_period: row.charge_period,
    charge_unit: row.scope === "per_reservation" ? "per_room" : row.charge_unit,
    scope: row.scope,
    min_nights: String(row.min_nights || "").trim() || null,
    max_nights: String(row.max_nights || "").trim() || null,
    min_pax: String(row.min_pax || "").trim() || null,
    max_pax: String(row.max_pax || "").trim() || null,
    amount: String(row.amount || "").trim(),
    amount_type: row.amount_type,
    inclusive: Boolean(row.inclusive),
    apply_automatically: Boolean(row.apply_automatically),
    is_mandatory: Boolean(row.is_mandatory),
    room_category_ids: normalizeIdArray(row.room_category_ids),
    room_rate_ids: normalizeIdArray(row.room_rate_ids),
    valid_from: row.valid_from || null,
    valid_to: row.valid_to || null,
    is_active: row.is_active !== false,
    sort_order: index,
  }));
}

function formatAmountValue(amount, amountType, currency, locale, catalog = []) {
  const numericValue = Number(amount);
  if (!Number.isFinite(numericValue)) return "-";
  if (amountType === "relative") return `${numericValue}%`;
  return formatCurrencyAmount(numericValue, currency, { locale, catalog });
}

export function buildOnboardingCurrencyOptions({
  catalog = [],
  configuration = null,
  existingRows = [],
} = {}) {
  const catalogRows = [
    ...(Array.isArray(catalog) ? catalog : []),
    ...(Array.isArray(configuration?.available_currencies)
      ? configuration.available_currencies
      : []),
  ];
  const byCode = new Map();
  catalogRows.forEach((entry) => {
    const code = normalizeCurrencyCode(entry?.code);
    if (!code || entry?.is_active === false) return;
    byCode.set(code, {
      ...entry,
      code,
      minor_units: getCurrencyMinorUnits(code, [entry]),
    });
  });

  const configuredCodes = Array.isArray(configuration?.currencies)
    ? configuration.currencies
        .map((entry) => normalizeCurrencyCode(entry?.code || entry?.currency_code || entry))
        .filter(Boolean)
    : [];
  const baseCurrencyCode = normalizeCurrencyCode(configuration?.base_currency_code);
  const existingCodes = existingRows
    .map((row) => normalizeCurrencyCode(row?.currency))
    .filter(Boolean);
  const preferredCodes = configuredCodes.length || baseCurrencyCode
    ? [baseCurrencyCode, ...configuredCodes, ...existingCodes]
    : [...byCode.keys(), ...existingCodes];

  return Array.from(new Set(preferredCodes.filter(Boolean))).map((code) => {
    const catalogEntry = byCode.get(code);
    return catalogEntry || {
      code,
      name: `${code} (existing draft)`,
      symbol: code,
      minor_units: getCurrencyMinorUnits(code),
      is_legacy: true,
    };
  });
}

export function getEmbeddedOnboardingCurrencyConfiguration(payload = {}) {
  const baseCurrencyCode = normalizeCurrencyCode(payload?.base_currency_code);
  const currencyRows = Array.isArray(payload?.currencies) ? payload.currencies : [];
  const currencyCodes = currencyRows
    .map((entry) => normalizeCurrencyCode(entry?.code || entry?.currency_code || entry))
    .filter(Boolean);
  if (!baseCurrencyCode || !currencyCodes.includes(baseCurrencyCode)) return null;
  return {
    base_currency_code: baseCurrencyCode,
    currencies: Array.from(new Set(currencyCodes)),
    available_currencies: currencyRows.filter(
      (entry) => entry && typeof entry === "object" && normalizeCurrencyCode(entry.code)
    ),
  };
}

function hasCurrencyMetadata(configuration, catalog = []) {
  const configuredCodes = Array.isArray(configuration?.currencies)
    ? configuration.currencies
        .map((entry) => normalizeCurrencyCode(entry?.code || entry?.currency_code || entry))
        .filter(Boolean)
    : [];
  const metadataCodes = new Set(
    [
      ...(Array.isArray(catalog) ? catalog : []),
      ...(Array.isArray(configuration?.available_currencies)
        ? configuration.available_currencies
        : []),
    ]
      .map((entry) => normalizeCurrencyCode(entry?.code))
      .filter(Boolean)
  );
  return configuredCodes.length > 0 && configuredCodes.every((code) => metadataCodes.has(code));
}

export function resolveOnboardingCurrencySources({
  payload = {},
  catalog = [],
  fallbackConfiguration = null,
} = {}) {
  const configuration =
    getEmbeddedOnboardingCurrencyConfiguration(payload) || fallbackConfiguration;
  const embeddedCatalog = Array.isArray(payload?.currencies)
    ? payload.currencies.filter((entry) => entry && typeof entry === "object")
    : [];
  const mergedCatalog = [
    ...(Array.isArray(catalog) ? catalog : []),
    ...embeddedCatalog,
  ];
  if (
    !configuration ||
    !normalizeCurrencyCode(configuration.base_currency_code) ||
    !Array.isArray(configuration.currencies) ||
    configuration.currencies.length === 0 ||
    !hasCurrencyMetadata(configuration, mergedCatalog)
  ) {
    return null;
  }
  return { configuration, catalog: mergedCatalog };
}

export function getOnboardingEditorCurrencyOptions(
  options = [],
  currentCurrency = "",
  isEditing = false
) {
  const currentCode = normalizeCurrencyCode(currentCurrency);
  return (Array.isArray(options) ? options : []).filter(
    (option) => !option?.is_legacy || (isEditing && option.code === currentCode)
  );
}

function formatDateValue(value, formatter) {
  if (!value) return "";
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? value : formatter.format(date);
}

function formatValidity(row, formatter, copy) {
  if (!row.valid_from && !row.valid_to) return copy.summaryNoValidity;
  if (row.valid_from && row.valid_to) {
    return `${formatDateValue(row.valid_from, formatter)} - ${formatDateValue(row.valid_to, formatter)}`;
  }
  if (row.valid_from) return copy.summaryFrom(formatDateValue(row.valid_from, formatter));
  return copy.summaryUntil(formatDateValue(row.valid_to, formatter));
}

function getChargeSummary(row, copy) {
  const chargeUnitKey = row.scope === "per_reservation" ? "per_room" : row.charge_unit;
  return [
    copy.options.scope[row.scope] || row.scope,
    copy.options.chargePeriod[row.charge_period] || row.charge_period,
    copy.options.chargeUnit[chargeUnitKey] || chargeUnitKey,
  ].join(" · ");
}

function getRestrictionsSummary(row, copy) {
  const parts = [];
  if (row.charge_period !== "per_stay" && (row.min_nights || row.max_nights)) {
    parts.push(copy.summaryNights(row.min_nights || "0", row.max_nights || "∞"));
  }
  if (row.scope !== "per_reservation" && row.charge_unit !== "per_room" && (row.min_pax || row.max_pax)) {
    parts.push(copy.summaryPax(row.min_pax || "0", row.max_pax || "∞"));
  }
  return parts.length ? parts.join(" · ") : copy.summaryNoRestrictions;
}

function getSelectStyles(isLightTheme) {
  return {
    control: (base, state) => ({
      ...base,
      minHeight: 48,
      borderRadius: 14,
      borderColor: state.isFocused
        ? isLightTheme
          ? "rgba(165, 126, 78, 0.58)"
          : "rgba(56, 189, 248, 0.58)"
        : isLightTheme
          ? "rgba(111, 94, 74, 0.18)"
          : "rgba(148, 163, 184, 0.28)",
      background: isLightTheme
        ? "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(246,240,232,0.96))"
        : "rgba(15, 23, 42, 0.86)",
      boxShadow: state.isFocused
        ? isLightTheme
          ? "0 0 0 3px rgba(191, 149, 100, 0.12)"
          : "0 0 0 3px rgba(56, 189, 248, 0.12)"
        : "none",
    }),
    valueContainer: (base) => ({ ...base, padding: "6px 10px", gap: 4 }),
    placeholder: (base) => ({ ...base, color: isLightTheme ? "#7b776f" : "rgba(226, 232, 240, 0.54)" }),
    input: (base) => ({ ...base, color: isLightTheme ? "#214150" : "#e2e8f0" }),
    singleValue: (base) => ({ ...base, color: isLightTheme ? "#214150" : "#e2e8f0" }),
    multiValue: (base) => ({
      ...base,
      borderRadius: 999,
      background: isLightTheme ? "rgba(191, 149, 100, 0.14)" : "rgba(34, 211, 238, 0.16)",
      border: isLightTheme
        ? "1px solid rgba(165, 126, 78, 0.18)"
        : "1px solid rgba(34, 211, 238, 0.24)",
    }),
    multiValueLabel: (base) => ({ ...base, color: isLightTheme ? "#6f5435" : "#c7f9ff", fontWeight: 700 }),
    multiValueRemove: (base) => ({ ...base, borderRadius: 999, color: isLightTheme ? "#8a5f38" : "#bae6fd" }),
    menu: (base) => ({
      ...base,
      borderRadius: 16,
      overflow: "hidden",
      background: isLightTheme ? "#fffaf4" : "#0f172a",
      boxShadow: isLightTheme
        ? "0 18px 36px rgba(95, 72, 40, 0.18)"
        : "0 20px 44px rgba(2, 6, 23, 0.56)",
      zIndex: 27000,
    }),
    menuPortal: (base) => ({ ...base, zIndex: 27000 }),
    option: (base, state) => ({
      ...base,
      background: state.isSelected
        ? isLightTheme
          ? "rgba(191, 149, 100, 0.18)"
          : "rgba(14, 165, 233, 0.24)"
        : state.isFocused
          ? isLightTheme
            ? "rgba(191, 149, 100, 0.1)"
            : "rgba(30, 41, 59, 0.9)"
          : isLightTheme
            ? "#fffaf4"
            : "#0f172a",
      color: isLightTheme ? "#214150" : "#e2e8f0",
    }),
  };
}

function FieldHelp({ text, ariaLabel, placement = "top-center" }) {
  return (
    <span className={`onboarding-helpWrap ${placement !== "top-center" ? `onboarding-helpWrap--${placement}` : ""}`}>
      <button type="button" className="onboarding-helpButton" aria-label={ariaLabel}>
        <FiInfo size={14} />
      </button>
      <span className="onboarding-helpTooltip" role="tooltip">
        {text}
      </span>
    </span>
  );
}

export default function ExtrasDiscountsStep() {
  const { languageId, themeId } = usePmsTheme();
  const { steps, markStepComplete, markStepIncomplete, setStepCommandState, clearStepCommandState } =
    useOnboardingFlow();
  const onboardingCopy = getOnboardingMessages(languageId);
  const copy = getExtrasDiscountsMessages(languageId);
  const [rows, setRows] = useState([]);
  const [references, setReferences] = useState({ room_categories: [], room_rates: [] });
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [sessionMeta, setSessionMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [feedback, setFeedback] = useState({ type: "", message: "" });
  const [showEditor, setShowEditor] = useState(false);
  const [editingRowKey, setEditingRowKey] = useState(null);
  const [editorRow, setEditorRow] = useState(createEmptyRow());
  const [editorFeedback, setEditorFeedback] = useState("");
  const nextClientKeyRef = useRef(1);
  const menuPortalTarget = typeof document !== "undefined" ? document.body : null;
  const selectStyles = useMemo(() => getSelectStyles(themeId === "light"), [themeId]);
  const stepNumber = useMemo(
    () => Math.max(steps.findIndex((step) => step.id === "extras-discounts") + 1, 1),
    [steps]
  );
  const categoryMap = useMemo(
    () => new Map(references.room_categories.map((category) => [Number(category.id), category])),
    [references.room_categories]
  );
  const rateMap = useMemo(
    () => new Map(references.room_rates.map((rate) => [Number(rate.id), rate])),
    [references.room_rates]
  );
  const dateFormatter = useMemo(
    () => new Intl.DateTimeFormat(languageId || "en", { dateStyle: "medium" }),
    [languageId]
  );

  const roomCategoryOptions = useMemo(
    () =>
      references.room_categories.map((category) => ({
        value: Number(category.id),
        label: category.name,
      })),
    [references.room_categories]
  );
  const roomRateOptions = useMemo(
    () =>
      references.room_rates
        .filter((rate) => {
          const selectedCategories = normalizeIdArray(editorRow.room_category_ids);
          return !selectedCategories.length || selectedCategories.includes(Number(rate.onboarding_room_category_id));
        })
        .map((rate) => ({
          value: Number(rate.id),
          label: rate.rate_code ? `${rate.rate_code} · ${rate.name}` : rate.name,
        })),
    [editorRow.room_category_ids, references.room_rates]
  );
  const selectedCategoryOptions = useMemo(
    () =>
      normalizeIdArray(editorRow.room_category_ids)
        .map((id) => {
          const category = categoryMap.get(Number(id));
          return category ? { value: Number(category.id), label: category.name } : null;
        })
        .filter(Boolean),
    [categoryMap, editorRow.room_category_ids]
  );
  const selectedRateOptions = useMemo(
    () =>
      normalizeIdArray(editorRow.room_rate_ids)
        .map((id) => {
          const rate = rateMap.get(Number(id));
          return rate
            ? { value: Number(rate.id), label: rate.rate_code ? `${rate.rate_code} · ${rate.name}` : rate.name }
            : null;
        })
        .filter(Boolean),
    [editorRow.room_rate_ids, rateMap]
  );
  const defaultCurrencyCode = currencyOptions[0]?.code || "";
  const editorCurrencyOptions = getOnboardingEditorCurrencyOptions(
    currencyOptions,
    editorRow.currency,
    Boolean(editingRowKey)
  );
  const editorMinorUnits = getCurrencyMinorUnits(editorRow.currency, currencyOptions);
  const hasNoExpiry = editorRow.valid_to === NO_EXPIRY_DATE;

  const loadDraft = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    setFeedback({ type: "", message: "" });
    try {
      const response = await axios.get("/api/onboarding/extras-discounts");
      const sourceRows = Array.isArray(response.data?.rows) ? response.data.rows : [];
      const businessUnitId = Number(response.data?.business_unit_id || 0);
      const embeddedConfiguration = getEmbeddedOnboardingCurrencyConfiguration(response.data);
      const [catalogOutcome, configurationOutcome] = await Promise.all([
        axios
          .get("/api/currencies")
          .then((catalogResponse) => ({ data: catalogResponse.data, error: null }))
          .catch((error) => ({ data: [], error })),
        embeddedConfiguration
          ? Promise.resolve({ data: embeddedConfiguration, error: null })
          : businessUnitId
          ? axios
              .get("/api/currency-settings", { params: { business_unit_id: businessUnitId } })
              .then((configurationResponse) => ({ data: configurationResponse.data, error: null }))
              .catch((error) => ({ data: null, error }))
          : Promise.resolve({ data: null, error: null }),
      ]);
      const resolvedCurrencySources = configurationOutcome.error
        ? null
        : resolveOnboardingCurrencySources({
            payload: response.data,
            catalog: catalogOutcome.data,
            fallbackConfiguration: configurationOutcome.data,
          });
      if (!resolvedCurrencySources) {
        throw new Error(copy.currencyLoadError);
      }
      const { configuration, catalog: mergedCatalog } = resolvedCurrencySources;
      const nextCurrencyOptions = buildOnboardingCurrencyOptions({
        catalog: mergedCatalog,
        configuration,
        existingRows: sourceRows,
      });
      const fallbackCurrency =
        normalizeCurrencyCode(configuration?.base_currency_code) ||
        nextCurrencyOptions[0]?.code ||
        "";
      setCurrencyOptions(nextCurrencyOptions);
      setRows(sourceRows.map((row, index) => normalizeIncomingRow(row, index, fallbackCurrency)));
      setReferences(normalizeReferences(response.data?.references));
      setSessionMeta(response.data?.session || null);
      setIsDirty(false);
      if (response.data?.session) markStepComplete("extras-discounts");
      else markStepIncomplete("extras-discounts");
    } catch (error) {
      console.error("[Onboarding][ExtrasDiscounts] Error loading draft:", error);
      setRows([]);
      setReferences({ room_categories: [], room_rates: [] });
      setCurrencyOptions([]);
      setSessionMeta(null);
      setLoadFailed(true);
      markStepIncomplete("extras-discounts");
      setFeedback({
        type: "error",
        message: error?.response?.data?.error || error?.message || copy.loadError,
      });
    } finally {
      setLoading(false);
    }
  }, [copy.currencyLoadError, copy.loadError, markStepComplete, markStepIncomplete]);

  useEffect(() => {
    loadDraft();
  }, [loadDraft]);

  const updateRows = (updater) => {
    setRows((currentRows) => (typeof updater === "function" ? updater(currentRows) : updater));
    setIsDirty(true);
    setFeedback({ type: "", message: "" });
  };

  const openCreateModal = () => {
    setEditingRowKey(null);
    setEditorRow(createEmptyRow(defaultCurrencyCode));
    setEditorFeedback("");
    setShowEditor(true);
  };

  const openEditModal = (row) => {
    setEditingRowKey(row.row_key);
    setEditorRow({ ...createEmptyRow(), ...row });
    setEditorFeedback("");
    setShowEditor(true);
  };

  const closeEditor = () => {
    setShowEditor(false);
    setEditingRowKey(null);
    setEditorRow(createEmptyRow(defaultCurrencyCode));
    setEditorFeedback("");
  };

  const updateEditorRow = (updater) => {
    setEditorRow((currentRow) =>
      typeof updater === "function" ? updater(currentRow) : { ...currentRow, ...updater }
    );
    setEditorFeedback("");
  };

  const handleCategoryChange = (selected) => {
    const nextCategoryIds = Array.isArray(selected) ? selected.map((option) => Number(option.value)) : [];
    const allowedRateIds = new Set(
      references.room_rates
        .filter(
          (rate) =>
            nextCategoryIds.length === 0 || nextCategoryIds.includes(Number(rate.onboarding_room_category_id))
        )
        .map((rate) => Number(rate.id))
    );
    updateEditorRow((currentRow) => ({
      ...currentRow,
      room_category_ids: nextCategoryIds,
      room_rate_ids: normalizeIdArray(currentRow.room_rate_ids).filter((rateId) =>
        allowedRateIds.has(Number(rateId))
      ),
    }));
  };

  const handleRateChange = (selected) => {
    const nextRateIds = Array.isArray(selected) ? selected.map((option) => Number(option.value)) : [];
    const allowedCategoryIds = new Set(
      references.room_rates
        .filter((rate) => nextRateIds.includes(Number(rate.id)))
        .map((rate) => Number(rate.onboarding_room_category_id))
    );
    updateEditorRow((currentRow) => ({
      ...currentRow,
      room_rate_ids: nextRateIds,
      room_category_ids:
        nextRateIds.length === 0
          ? normalizeIdArray(currentRow.room_category_ids)
          : normalizeIdArray(currentRow.room_category_ids).filter((categoryId) =>
              allowedCategoryIds.has(Number(categoryId))
            ),
    }));
  };

  const saveEditorRow = () => {
    if (!String(editorRow.name || "").trim()) {
      setEditorFeedback(copy.validationName);
      return;
    }
    if (!String(editorRow.amount || "").trim() || !Number.isFinite(Number(editorRow.amount))) {
      setEditorFeedback(copy.validationAmount);
      return;
    }
    const normalizedCurrency = normalizeCurrencyCode(editorRow.currency);
    if (!normalizedCurrency) {
      setEditorFeedback(copy.validationCurrency);
      return;
    }
    if (
      editorRow.amount_type === "absolute" &&
      !hasValidCurrencyPrecision(editorRow.amount, normalizedCurrency, currencyOptions)
    ) {
      setEditorFeedback(copy.validationMinorUnits(normalizedCurrency, editorMinorUnits));
      return;
    }

    const normalizedRow = {
      ...editorRow,
      name: String(editorRow.name || "").trim(),
      description: String(editorRow.description || "").trim(),
      currency: normalizedCurrency,
      room_category_ids: normalizeIdArray(editorRow.room_category_ids),
      room_rate_ids: normalizeIdArray(editorRow.room_rate_ids),
      row_key: editorRow.row_key || editingRowKey || `client-${Date.now()}-${nextClientKeyRef.current++}`,
    };

    updateRows((currentRows) =>
      editingRowKey
        ? currentRows.map((row) => (row.row_key === editingRowKey ? normalizedRow : row))
        : [...currentRows, normalizedRow]
    );
    closeEditor();
  };

  const handleSave = useCallback(async () => {
    setSaving(true);
    setFeedback({ type: "", message: "" });
    try {
      const response = await axios.put("/api/onboarding/extras-discounts", { rows: serializeRows(rows) });
      setRows(
        (Array.isArray(response.data?.rows) ? response.data.rows : []).map((row, index) =>
          normalizeIncomingRow(row, index, defaultCurrencyCode)
        )
      );
      setReferences(normalizeReferences(response.data?.references));
      setSessionMeta(response.data?.session || null);
      setIsDirty(false);
      markStepComplete("extras-discounts");
      setFeedback({ type: "success", message: copy.saveSuccess });
      return true;
    } catch (error) {
      console.error("[Onboarding][ExtrasDiscounts] Error saving draft:", error);
      setFeedback({ type: "error", message: error?.response?.data?.error || copy.saveError });
      return false;
    } finally {
      setSaving(false);
    }
  }, [copy.saveError, copy.saveSuccess, defaultCurrencyCode, markStepComplete, rows]);

  useEffect(() => {
    setStepCommandState("extras-discounts", {
      onSave: handleSave,
      canSave: !loading && !loadFailed && !showEditor,
      canProceed: !isDirty && !loadFailed && !showEditor,
      isDirty,
      isSaving: saving,
    });
    return () => clearStepCommandState("extras-discounts");
  }, [clearStepCommandState, handleSave, isDirty, loadFailed, loading, saving, setStepCommandState, showEditor]);

  const formattedSavedAt = useMemo(() => {
    if (!sessionMeta?.updated_at) return copy.summaryNotSaved;
    const parsedDate = new Date(sessionMeta.updated_at);
    return Number.isNaN(parsedDate.getTime())
      ? copy.summaryNotSaved
      : new Intl.DateTimeFormat(languageId || "en", { dateStyle: "short", timeStyle: "short" }).format(parsedDate);
  }, [copy.summaryNotSaved, languageId, sessionMeta?.updated_at]);

  if (loading) {
    return (
      <div className="onboarding-emptyState">
        <h3 className="onboarding-sectionTitle">{copy.title}</h3>
        <p className="onboarding-sectionText">{copy.copy}</p>
      </div>
    );
  }

  if (loadFailed) {
    return (
      <div className="onboarding-emptyState onboarding-formCard--centered">
        <div className="onboarding-eyebrow">{copy.eyebrow}</div>
        <h3 className="onboarding-sectionTitle">{copy.title}</h3>
        <p className="onboarding-sectionText">{feedback.message || copy.loadError}</p>
      </div>
    );
  }

  return (
    <>
      <div className="onboarding-grid">
        <section className="onboarding-formCard">
          <div className="onboarding-formHeader">
            <div>
              <div className="onboarding-eyebrow">{copy.eyebrow}</div>
              <div className="onboarding-stepCount">{onboardingCopy.progress.stepCounter(stepNumber, steps.length)}</div>
              <h3 className="onboarding-sectionTitle">{copy.title}</h3>
              <p className="onboarding-sectionText">{copy.copy}</p>
            </div>
          </div>

          <div className="onboarding-settingsStack">
            <section className="onboarding-settingsSection">
              <div className="onboarding-settingsSectionHead">
                <div>
                  <div className="onboarding-extraHeadingRow">
                    <h4>{copy.configuredTitle}</h4>
                    <FieldHelp text={copy.optionalInfoText} ariaLabel={copy.optionalInfoAria} placement="bottom-start" />
                  </div>
                  <p>{copy.configuredCopy}</p>
                </div>
              </div>

              <div className="onboarding-settingsNotice">{copy.optionalNotice}</div>

              <div className="onboarding-toolbar onboarding-toolbar--compact">
                <button type="button" className="onboarding-primaryBtn" onClick={openCreateModal}>
                  <FiPlus size={16} />
                  {copy.add}
                </button>
              </div>

              {rows.length ? (
                <div className="onboarding-extraGrid">
                  {rows.map((row) => {
                    const categoryNames = normalizeIdArray(row.room_category_ids)
                      .map((id) => categoryMap.get(Number(id))?.name)
                      .filter(Boolean);
                    const rateNames = normalizeIdArray(row.room_rate_ids)
                      .map((id) => {
                        const rate = rateMap.get(Number(id));
                        return rate ? (rate.rate_code ? `${rate.rate_code} · ${rate.name}` : rate.name) : null;
                      })
                      .filter(Boolean);
                    const flags = [];
                    if (row.inclusive) flags.push(copy.summaryFlags.inclusive);
                    if (row.apply_automatically) flags.push(copy.summaryFlags.automatic);
                    if (row.is_mandatory) flags.push(copy.summaryFlags.mandatory);
                    if (!row.is_active) flags.push(copy.summaryFlags.inactive);

                    return (
                      <article key={row.row_key} className={`onboarding-extraCard ${row.is_active ? "" : "is-inactive"}`}>
                        <div className="onboarding-extraCardHead">
                          <div className="onboarding-extraCardTitleWrap">
                            <div className="onboarding-extraCardTitleRow">
                              <h4>{row.name}</h4>
                              <span className={`onboarding-settingsRoleBadge ${row.is_active ? "onboarding-settingsRoleBadge--highlight" : ""}`}>
                                {row.is_active ? copy.active : copy.inactive}
                              </span>
                            </div>
                            <p>{row.description || copy.noDescription}</p>
                          </div>

                          <div className="onboarding-rateActionGroup">
                            <button type="button" className="onboarding-tableAction" onClick={() => openEditModal(row)} aria-label={copy.edit} title={copy.edit}>
                              <FiEdit2 size={15} />
                            </button>
                            <button
                              type="button"
                              className="onboarding-tableAction"
                              onClick={() =>
                                updateRows((currentRows) =>
                                  currentRows.map((currentRow) =>
                                    currentRow.row_key === row.row_key
                                      ? { ...currentRow, is_active: !currentRow.is_active }
                                      : currentRow
                                  )
                                )
                              }
                              aria-label={row.is_active ? copy.deactivate : copy.activate}
                              title={row.is_active ? copy.deactivate : copy.activate}
                            >
                              <FiPower size={15} />
                            </button>
                            <button
                              type="button"
                              className="onboarding-tableAction"
                              onClick={() => updateRows((currentRows) => currentRows.filter((currentRow) => currentRow.row_key !== row.row_key))}
                              aria-label={copy.remove}
                              title={copy.remove}
                            >
                              <FiTrash2 size={15} />
                            </button>
                          </div>
                        </div>

                        <div className="onboarding-extraMetaGrid">
                          <div className="onboarding-extraMetaItem">
                            <span>{copy.summaryAmountLabel}</span>
                            <strong>{formatAmountValue(row.amount, row.amount_type, row.currency, languageId || "en", currencyOptions)}</strong>
                          </div>
                          <div className="onboarding-extraMetaItem">
                            <span>{copy.summaryChargeLabel}</span>
                            <strong>{getChargeSummary(row, copy)}</strong>
                          </div>
                          <div className="onboarding-extraMetaItem">
                            <span>{copy.summaryValidityLabel}</span>
                            <strong>{formatValidity(row, dateFormatter, copy)}</strong>
                          </div>
                        </div>

                        <div className="onboarding-extraSummaryList">
                          <div className="onboarding-extraSummaryLine">
                            <span>{copy.summaryRoomCategoriesLabel}</span>
                            <strong>{categoryNames.length ? categoryNames.join(", ") : copy.summaryAllInventory}</strong>
                          </div>
                          <div className="onboarding-extraSummaryLine">
                            <span>{copy.summaryRoomRatesLabel}</span>
                            <strong>{rateNames.length ? rateNames.join(", ") : copy.summaryNoSelection}</strong>
                          </div>
                          <div className="onboarding-extraSummaryLine">
                            <span>{copy.summaryRestrictionsLabel}</span>
                            <strong>{getRestrictionsSummary(row, copy)}</strong>
                          </div>
                          <div className="onboarding-extraSummaryLine onboarding-extraSummaryLine--flags">
                            <span>{copy.summaryFlagsLabel}</span>
                            <div className="onboarding-extraFlags">
                              {flags.length ? (
                                flags.map((flag) => (
                                  <span key={`${row.row_key}-${flag}`} className="onboarding-extraFlag">
                                    {flag}
                                  </span>
                                ))
                              ) : (
                                <strong>{copy.summaryNoFlags}</strong>
                              )}
                            </div>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              ) : (
                <div className="onboarding-emptyState onboarding-emptyState--compact">
                  <h4 className="onboarding-sectionTitle">{copy.emptyTitle}</h4>
                  <p className="onboarding-sectionText">{copy.emptyCopy}</p>
                </div>
              )}

              {feedback.message ? (
                <div className={`onboarding-status ${feedback.type === "success" ? "onboarding-status--success" : "onboarding-status--error"}`}>
                  {feedback.message}
                </div>
              ) : null}
            </section>
          </div>
        </section>

        <aside className="onboarding-infoCard">
          <div className="onboarding-eyebrow">{copy.eyebrow}</div>
          <h3 className="onboarding-sectionTitle">{copy.summaryTitle}</h3>
          <p className="onboarding-sectionText">{copy.summaryIntro}</p>
          <div className="onboarding-pointsList">
            {copy.summaryPoints.map((point) => (
              <div key={point} className="onboarding-pointItem">
                <span className="onboarding-pointDot" />
                <span>{point}</span>
              </div>
            ))}
          </div>
          <div className="onboarding-kpi"><span className="onboarding-kpiLabel">{copy.summaryConfiguredLabel}</span><span className="onboarding-kpiValue">{rows.length}</span></div>
          <div className="onboarding-kpi"><span className="onboarding-kpiLabel">{copy.summaryAutomaticLabel}</span><span className="onboarding-kpiValue">{rows.filter((row) => row.apply_automatically).length}</span></div>
          <div className="onboarding-kpi"><span className="onboarding-kpiLabel">{copy.summaryMandatoryLabel}</span><span className="onboarding-kpiValue">{rows.filter((row) => row.is_mandatory).length}</span></div>
          <div className="onboarding-kpi"><span className="onboarding-kpiLabel">{copy.summaryInclusiveLabel}</span><span className="onboarding-kpiValue">{rows.filter((row) => row.inclusive).length}</span></div>
          <div className="onboarding-kpi"><span className="onboarding-kpiLabel">{copy.summarySavedLabel}</span><span className="onboarding-kpiValue">{formattedSavedAt}</span></div>
        </aside>
      </div>

      {showEditor ? (
        <div className="onboarding-entryModal__backdrop" role="presentation">
          <div className="onboarding-entryModal onboarding-entryModal--wide" role="dialog" aria-modal="true">
            <div className="onboarding-entryModal__header">
              <div>
                <div className="onboarding-eyebrow">{copy.modalKicker}</div>
                <h4>{editingRowKey ? copy.modalEditTitle : copy.modalCreateTitle}</h4>
                <p className="onboarding-entryModal__copy">{copy.modalCopy}</p>
              </div>
            </div>

            <div className="onboarding-entryModal__hint">{copy.modalDiscountHint}</div>

            <div className="onboarding-entryModal__body onboarding-extraModalLayout">
              <div className="onboarding-extraModalMain">
                <section className="onboarding-rateEditorSection">
                  <div className="onboarding-rateEditorSectionHead"><h5>{copy.commercialTitle}</h5><p>{copy.commercialCopy}</p></div>
                  <div className="onboarding-fields onboarding-fields--threeCols">
                    <label className="onboarding-field onboarding-field--full"><span className="onboarding-label">{copy.fields.name}</span><input className="onboarding-input" type="text" value={editorRow.name} placeholder={copy.placeholders.name} onChange={(event) => updateEditorRow({ name: event.target.value })} /></label>
                    <label className="onboarding-field"><span className="onboarding-label">{copy.fields.amount}</span><input className="onboarding-input" type="number" step={editorRow.amount_type === "relative" ? "0.01" : currencyAmountStep(editorRow.currency, currencyOptions)} value={editorRow.amount} onChange={(event) => updateEditorRow({ amount: event.target.value })} /></label>
                    <label className="onboarding-field"><span className="onboarding-label">{copy.fields.currency}</span><select className="onboarding-input" value={editorRow.currency} onChange={(event) => updateEditorRow({ currency: event.target.value })} disabled={!editorCurrencyOptions.length}><option value="" disabled>{editorCurrencyOptions.length ? copy.placeholders.currency : copy.placeholders.noCurrencies}</option>{editorCurrencyOptions.map((currency) => <option value={currency.code} key={currency.code}>{currency.code} · {currency.name}</option>)}</select></label>
                    <label className="onboarding-field"><span className="onboarding-label">{copy.fields.amountType}</span><select className="onboarding-input" value={editorRow.amount_type} onChange={(event) => updateEditorRow({ amount_type: event.target.value })}><option value="absolute">{copy.options.amountType.absolute}</option><option value="relative">{copy.options.amountType.relative}</option></select></label>
                    <label className="onboarding-field onboarding-field--full"><span className="onboarding-label">{copy.fields.description}</span><textarea className="onboarding-textarea" value={editorRow.description} placeholder={copy.placeholders.description} onChange={(event) => updateEditorRow({ description: event.target.value })} /></label>
                  </div>
                </section>

                <section className="onboarding-rateEditorSection">
                  <div className="onboarding-rateEditorSectionHead"><h5>{copy.logicTitle}</h5><p>{copy.logicCopy}</p></div>
                  <div className="onboarding-fields onboarding-fields--threeCols">
                    <label className="onboarding-field"><span className="onboarding-label">{copy.fields.scope}</span><select className="onboarding-input" value={editorRow.scope} onChange={(event) => updateEditorRow((currentRow) => ({ ...currentRow, scope: event.target.value, charge_unit: event.target.value === "per_reservation" ? "per_room" : currentRow.charge_unit }))}><option value="per_room">{copy.options.scope.per_room}</option><option value="per_reservation">{copy.options.scope.per_reservation}</option></select></label>
                    <label className="onboarding-field"><span className="onboarding-label">{copy.fields.chargePeriod}</span><select className="onboarding-input" value={editorRow.charge_period} onChange={(event) => updateEditorRow({ charge_period: event.target.value })}><option value="per_stay">{copy.options.chargePeriod.per_stay}</option><option value="per_night">{copy.options.chargePeriod.per_night}</option></select></label>
                    <label className="onboarding-field"><span className="onboarding-label">{copy.fields.chargeUnit}</span><select className="onboarding-input" value={editorRow.scope === "per_reservation" ? "per_room" : editorRow.charge_unit} disabled={editorRow.scope === "per_reservation"} onChange={(event) => updateEditorRow({ charge_unit: event.target.value })}><option value="per_room">{copy.options.chargeUnit.per_room}</option><option value="per_adult">{copy.options.chargeUnit.per_adult}</option><option value="per_child">{copy.options.chargeUnit.per_child}</option><option value="per_pax">{copy.options.chargeUnit.per_pax}</option></select></label>
                    <label className="onboarding-field"><span className="onboarding-label">{copy.fields.validFrom}</span><input className="onboarding-input" type="date" value={editorRow.valid_from} onChange={(event) => updateEditorRow({ valid_from: event.target.value })} /></label>
                    <label className="onboarding-field"><span className="onboarding-label">{copy.fields.validTo}</span><div className="onboarding-inlineDateControl"><input className="onboarding-input" type="date" value={editorRow.valid_to} disabled={hasNoExpiry} onChange={(event) => updateEditorRow({ valid_to: event.target.value })} /><label className="onboarding-inlineCheckbox"><input type="checkbox" checked={hasNoExpiry} onChange={(event) => updateEditorRow({ valid_to: event.target.checked ? NO_EXPIRY_DATE : "" })} /><span>({copy.fields.noExpiry})</span></label></div></label>
                  </div>
                </section>

                <section className="onboarding-rateEditorSection">
                  <div className="onboarding-rateEditorSectionHead"><h5>{copy.targetingTitle}</h5><p>{copy.targetingCopy}</p></div>
                  <div className="onboarding-fields">
                    <div className="onboarding-field"><span className="onboarding-label">{copy.fields.roomCategories}</span><Select isMulti options={roomCategoryOptions} value={selectedCategoryOptions} onChange={handleCategoryChange} styles={selectStyles} menuPortalTarget={menuPortalTarget} closeMenuOnSelect={false} placeholder={copy.placeholders.roomCategories} noOptionsMessage={() => copy.placeholders.noOptions} /></div>
                    <div className="onboarding-field"><span className="onboarding-label">{copy.fields.roomRates}</span><Select isMulti options={roomRateOptions} value={selectedRateOptions} onChange={handleRateChange} styles={selectStyles} menuPortalTarget={menuPortalTarget} closeMenuOnSelect={false} placeholder={copy.placeholders.roomRates} noOptionsMessage={() => copy.placeholders.noOptions} /></div>
                  </div>
                  <div className="onboarding-entryModal__hint onboarding-extraModalNote">{references.room_categories.length || references.room_rates.length ? copy.targetingHint : copy.referencesHint}</div>
                </section>

                <section className="onboarding-rateEditorSection">
                  <div className="onboarding-rateEditorSectionHead"><h5>{copy.restrictionsTitle}</h5><p>{copy.restrictionsCopy}</p></div>
                  <div className="onboarding-fields onboarding-fields--fourCols">
                      <label className="onboarding-field"><span className="onboarding-label">{copy.fields.minNights}</span><input className="onboarding-input" type="number" value={editorRow.min_nights} onChange={(event) => updateEditorRow({ min_nights: event.target.value })} /></label>
                      <label className="onboarding-field"><span className="onboarding-label">{copy.fields.maxNights}</span><input className="onboarding-input" type="number" value={editorRow.max_nights} onChange={(event) => updateEditorRow({ max_nights: event.target.value })} /></label>
                      <label className="onboarding-field"><span className="onboarding-label">{copy.fields.minPax}</span><input className="onboarding-input" type="number" value={editorRow.min_pax} onChange={(event) => updateEditorRow({ min_pax: event.target.value })} /></label>
                      <label className="onboarding-field"><span className="onboarding-label">{copy.fields.maxPax}</span><input className="onboarding-input" type="number" value={editorRow.max_pax} onChange={(event) => updateEditorRow({ max_pax: event.target.value })} /></label>
                  </div>
                </section>
              </div>

              <aside className="onboarding-extraModalAside">
                <section className="onboarding-settingsSection">
                  <div className="onboarding-rateEditorSectionHead"><h5>{copy.switchesTitle}</h5><p>{copy.switchesCopy}</p></div>
                  <div className="onboarding-extraCheckboxStack">
                    <label className="onboarding-extraCheckbox"><input type="checkbox" checked={editorRow.inclusive} onChange={(event) => updateEditorRow({ inclusive: event.target.checked })} /><span>{copy.fields.inclusive}</span></label>
                    <label className="onboarding-extraCheckbox"><input type="checkbox" checked={editorRow.apply_automatically} onChange={(event) => updateEditorRow({ apply_automatically: event.target.checked })} /><span>{copy.fields.automatic}</span></label>
                    <label className="onboarding-extraCheckbox"><input type="checkbox" checked={editorRow.is_mandatory} onChange={(event) => updateEditorRow({ is_mandatory: event.target.checked })} /><span>{copy.fields.mandatory}</span></label>
                  </div>
                </section>

                <section className="onboarding-settingsSection">
                  <div className="onboarding-rateEditorSectionHead"><h5>{copy.summaryTitle}</h5><p>{copy.summaryCopy}</p></div>
                  <div className="onboarding-extraSummaryGrid">
                    <div className="onboarding-kpi"><span className="onboarding-kpiLabel">{copy.summaryAmountLabel}</span><span className="onboarding-kpiValue">{editorRow.amount ? formatAmountValue(editorRow.amount, editorRow.amount_type, editorRow.currency, languageId || "en", currencyOptions) : "-"}</span></div>
                    <div className="onboarding-kpi"><span className="onboarding-kpiLabel">{copy.summaryChargeLabel}</span><span className="onboarding-kpiValue">{getChargeSummary(editorRow, copy)}</span></div>
                    <div className="onboarding-kpi"><span className="onboarding-kpiLabel">{copy.summaryValidityLabel}</span><span className="onboarding-kpiValue">{formatValidity(editorRow, dateFormatter, copy)}</span></div>
                    <div className="onboarding-kpi"><span className="onboarding-kpiLabel">{copy.summaryRestrictionsLabel}</span><span className="onboarding-kpiValue">{getRestrictionsSummary(editorRow, copy)}</span></div>
                  </div>
                </section>
              </aside>
            </div>

            {editorFeedback ? <div className="onboarding-status onboarding-status--error">{editorFeedback}</div> : null}

            <div className="onboarding-entryModal__footer">
              <button type="button" className="onboarding-ghostBtn" onClick={closeEditor}>{copy.cancel}</button>
              <button type="button" className="onboarding-primaryBtn" onClick={saveEditorRow}>{editingRowKey ? copy.modalSaveUpdate : copy.modalSaveCreate}</button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
